import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

import { reportarEvento, type EventoSeguridad } from '@/lib/seguridad';
import { cn } from '@/lib/utils';

/**
 * Visor de contenido protegido (skill §18). Disuade y deja rastro; no puede impedir capturas del
 * sistema operativo. La marca de agua del alumno la estampa la API en la imagen.
 * - Velo al perder el foco, al ocultar la pestaña, con PrintScreen y con DevTools abierto.
 * - Bloquea clic derecho, arrastre, copiar, imprimir y guardar; `@media print` oculta todo.
 */
export function ContenidoProtegido({
  contenido,
  children,
  className,
  mensajeVelo = 'El contenido se ocultó porque saliste de la ventana. Haz clic aquí para continuar.',
}: {
  /** Tipo e id del contenido para el reporte, p. ej. `['presentation', '910004']`. */
  contenido: [string, string | number];
  children: ReactNode;
  className?: string;
  mensajeVelo?: string;
}) {
  const [velado, setVelado] = useState(false);
  const ultimo = useRef<Record<string, number>>({});
  const [tipo, id] = contenido;

  const reportar = useCallback(
    (evento: EventoSeguridad) => {
      const ahora = Date.now();
      if (ahora - (ultimo.current[evento] ?? 0) < 10_000) return; // antirrebote por tipo
      ultimo.current[evento] = ahora;
      reportarEvento(evento, tipo, String(id));
    },
    [tipo, id],
  );

  useEffect(() => {
    const ocultar = () => {
      setVelado(true);
      reportar('focus_lost_viewer');
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') ocultar();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        setVelado(true);
        void navigator.clipboard.writeText('').catch(() => undefined);
        reportar('print_screen');
      }
      if ((e.ctrlKey || e.metaKey) && ['p', 's', 'c'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        reportar(e.key.toLowerCase() === 'p' ? 'print_attempt' : 'copy_attempt');
      }
    };
    const devtools = window.setInterval(() => {
      const abierto =
        window.outerWidth - window.innerWidth > 200 ||
        window.outerHeight - window.innerHeight > 200;
      if (abierto) {
        setVelado(true);
        reportar('devtools');
      }
    }, 2_000);
    window.addEventListener('blur', ocultar);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('keyup', onKey);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('blur', ocultar);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('keyup', onKey);
      window.removeEventListener('keydown', onKey);
      window.clearInterval(devtools);
    };
  }, [reportar]);

  return (
    <div
      className={cn('contenido-protegido anticopia relative overflow-hidden', className)}
      onContextMenu={(e) => {
        e.preventDefault();
        reportar('context_menu');
      }}
      onDragStart={(e) => {
        e.preventDefault();
      }}
      onCopy={(e) => {
        e.preventDefault();
        reportar('copy_attempt');
      }}
    >
      {children}
      {velado && (
        <button
          type="button"
          className="velo-proteccion animate__animated animate__fadeIn"
          onClick={() => {
            setVelado(false);
          }}
        >
          <span className="max-w-sm text-sm font-bold">{mensajeVelo}</span>
        </button>
      )}
    </div>
  );
}

/**
 * Lámina protegida: la API la entrega estampada con la marca del alumno (URL firmada de 60 s,
 * ligada a la sesión) y aquí se pinta en `<canvas>` para que no quede un `<img>` descargable.
 * Solo se vuelve a descargar cuando cambia `clave` (la lámina), no cuando la consulta renueva
 * las URLs firmadas: así el visor puede refrescarlas cada 45 s sin parpadeos. Si una URL venció
 * (p. ej., la pestaña estuvo en segundo plano y no se renovó), `onExpirada` pide URLs nuevas: se
 * reintenta solo una vez por lámina y después queda el botón «Reintentar». Solo se descarga al
 * acercarse a la pantalla: una rejilla de cientos de portadas no agota el límite por minuto de la
 * API ni deja que venzan las URLs que esperan turno.
 */
export function ImagenProtegida({
  src,
  clave = src,
  alt,
  className,
  onExpirada,
}: {
  src: string;
  clave?: string | number;
  alt: string;
  className?: string;
  onExpirada?: () => unknown;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const marco = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined');
  const [intento, setIntento] = useState(0);
  // Resultado de la última descarga; si es de otra lámina o intento, la actual sigue cargando.
  const [resultado, setResultado] = useState<{ k: string; ok: boolean } | null>(null);
  const k = `${String(clave)}:${String(intento)}`;
  const estado = resultado?.k !== k ? 'cargando' : resultado.ok ? 'lista' : 'error';
  const ultimaSrc = useRef(src);
  const ultimaExpirada = useRef(onExpirada);
  const autoReintentada = useRef<string | number | null>(null);
  useLayoutEffect(() => {
    ultimaSrc.current = src;
    ultimaExpirada.current = onExpirada;
  });
  const renovar = useCallback(async () => {
    try {
      await ultimaExpirada.current?.();
    } finally {
      setIntento((n) => n + 1);
    }
  }, []);
  useEffect(() => {
    const el = marco.current;
    if (visible || !el) return;
    const io = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px 0px' },
    );
    io.observe(el);
    return () => {
      io.disconnect();
    };
  }, [visible]);
  useEffect(() => {
    if (!visible) return;
    const src = ultimaSrc.current;
    let vivo = true;
    const setEstado = (e: 'lista' | 'error') => {
      setResultado({ k, ok: e === 'lista' });
    };
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => {
      const c = canvas.current;
      if (!vivo || !c) return;
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      c.getContext('2d')?.drawImage(img, 0, 0);
      setEstado('lista');
      // Suelta la referencia; sin `onerror`, porque vaciar `src` dispara un error.
      img.onerror = null;
      img.src = '';
    };
    img.onerror = () => {
      if (!vivo) return;
      // Lo más común es una URL firmada vencida: renueva y reintenta una vez sin mostrar error.
      if (ultimaExpirada.current && autoReintentada.current !== clave) {
        autoReintentada.current = clave;
        void renovar();
        return;
      }
      setEstado('error');
    };
    img.src = src;
    return () => {
      vivo = false;
    };
  }, [k, clave, renovar, visible]);
  return (
    <div ref={marco} className="relative">
      <canvas
        ref={canvas}
        role="img"
        aria-label={alt}
        className={cn('block h-auto w-full', estado !== 'lista' && 'invisible', className)}
      />
      {estado === 'cargando' && (
        <div className="absolute inset-0 animate-pulse rounded-[inherit] bg-white/5" aria-hidden />
      )}
      {estado === 'error' && (
        <div className="absolute inset-0 grid place-items-center text-center text-sm text-texto-2">
          <div>
            <p>No pudimos cargar esta imagen.</p>
            {onExpirada && (
              <button
                type="button"
                className="btn-cs btn-contorno mt-2"
                onClick={() => void renovar()}
              >
                Reintentar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
